import React, { useState } from 'react';
import { Send, CheckCircle, Shield } from 'lucide-react';
import './WhistleblowerForm.css';
import { useTranslation } from "react-i18next";

export function WhistleblowerForm() {
    const { t } = useTranslation();

    const TYPE_OPTIONS = [
        { value: "Assédio Moral", label: t('forms.whistleblower.types.moral_harassment', 'Assédio Moral') },
        { value: "Assédio Sexual", label: t('forms.whistleblower.types.sexual_harassment', 'Assédio Sexual') },
        { value: "Discriminação", label: t('forms.whistleblower.types.discrimination', 'Discriminação') },
        { value: "Fraude", label: t('forms.whistleblower.types.fraud', 'Fraude') },
        { value: "Conflito de Interesses", label: t('forms.whistleblower.types.conflict_of_interest', 'Conflito de Interesses') },
        { value: "Violação do Código de Ética", label: t('forms.whistleblower.types.ethics_violation', 'Violação do Código de Ética') }
    ];

    const [formData, setFormData] = useState({
        type: '',
        description: '',
        userEmail: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.type || !formData.description) {
            alert(t('forms.whistleblower.required_error', 'Por favor, preencha todos os campos obrigatórios.'));
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await fetch('https://backend.calriz.com/api/denuncia', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                setSubmitStatus('success');
                setFormData({ type: '', description: '', userEmail: '' });
            } else {
                throw new Error('Falha no envio');
            }
        } catch (error) {
            console.error('Erro ao enviar denúncia:', error);
            setSubmitStatus('error');
            alert(t('forms.whistleblower.error', 'Ocorreu um erro ao enviar sua denúncia. Por favor, tente novamente.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitStatus === 'success') {
        return (
            <div className="success-container">
                <div className="success-icon-wrapper">
                    <CheckCircle size={32} />
                </div>
                <h3 className="success-title">{t('forms.whistleblower.success_title', 'Denúncia Enviada com Sucesso')}</h3>
                <p className="success-text">
                    {t('forms.whistleblower.success_text', 'Agradecemos sua colaboração em manter nosso ambiente de trabalho ético e seguro. Sua denúncia será analisada com total confidencialidade.')}
                </p>
                <button
                    onClick={() => setSubmitStatus(null)}
                    className="new-report-button"
                >
                    {t('forms.whistleblower.new_report', 'Enviar nova denúncia')}
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="whistleblower-form-container">

            {/* Identity Notice */}
            <div className="identity-notice">
                <Shield className="notice-icon" size={20} />
                <div className="notice-content">
                    <h4>{t('forms.whistleblower.identity_title', 'Identificação do Relator')}</h4>
                    <p>
                        {t('forms.whistleblower.identity_text', 'Este canal é seguro e confidencial. Se desejar, você pode fornecer seu e-mail abaixo para contato, mas isso é opcional. Se deixado em branco, sua denúncia será totalmente anônima.')}
                    </p>
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="userEmail" className="form-label">
                    {t('forms.whistleblower.email_label', 'Seu E-mail (Opcional)')}
                </label>
                <input
                    type="email"
                    id="userEmail"
                    name="userEmail"
                    value={formData.userEmail}
                    onChange={handleChange}
                    className="form-input"
                    placeholder={t('forms.whistleblower.email_placeholder', 'exemplo@email.com')}
                />
            </div>

            <div className="form-group">
                <label htmlFor="type" className="form-label">
                    {t('forms.whistleblower.type_label', 'Tipo de Denúncia*')}
                </label>
                <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="form-select"
                    required
                >
                    <option value="">{t('forms.whistleblower.select_type', 'Selecione o tipo de irregularidade')}</option>
                    {TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </div>

            <div className="form-group">
                <label htmlFor="description" className="form-label">
                    {t('forms.whistleblower.description_label', 'Descrição do Ocorrido*')}
                </label>
                <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={6}
                    className="form-textarea"
                    placeholder={t('forms.whistleblower.description_placeholder', 'Descreva detalhadamente o ocorrido. Inclua datas, locais e nomes, se possível. Quanto mais detalhes, melhor poderemos apurar.')}
                    required
                />
            </div>

            <div className="pt-4">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="submit-button"
                >
                    {isSubmitting ? (
                        t('forms.sending', 'Enviando...')
                    ) : (
                        <>
                            <Send size={18} />
                            {t('forms.whistleblower.submit', 'Enviar Denúncia')}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}
